/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : DocumentLibraryIndex.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { useDispatch, useSelector } from "react-redux";
import { Box, Grid, Typography } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import ProjectvariantSearchbar from "../../../components/customComponentsV2/common/projectSearchbar/ProjectvariantSearchbar";
import CommonBreadcrumbs from "../../../components/customComponentsV2/common/Breadcrumbs/CommonBreadcrumbs";
import CustomSearchbarWithApiV2 from "../../../components/customComponentsV2/common/searchBar/CustomSearchbarWithApiV2";
import documentLibraryAction from "../../../redux/actions/documentLibraryAction";
import DocumentLibraryCard from "./DocumentLibraryCard";
import ViewDynammicFormDetails from "../../admin-settings/DynamicForms/ViewDynammicFormDetails";
import dynamicTemplateAction from "../../../redux/actions/dynamicTemplateAction";
import { HandleApiActions } from "../../../redux/actions/actionHandler";
import UpdateDynamicForm from "../../admin-settings/DynamicForms/UpdateDynamicForm";
import CustomPaginationV2 from "../../../components/customComponentsV2/common/pagination/CustomPaginationV2";
import SortTemplate from "../../../components/customComponentsV2/common/Templates/SortTemplate";
import CustomFilterV2 from "../../../components/customComponentsV2/common/filter/CustomFilterV2";

import FilterTemplate from "../../../components/customComponentsV2/common/Templates/FilterTemplate";
import PreviewModal from "../../admin-settings/DynamicFormV2/PreviewModal";
import testCasesActions from "../../../redux/actions/testCasesActions";
import projectConfigAPI from "../../../redux/actions/projectConfigAPI";
import { toast } from "react-toastify";
import CustomFilesUploadButton from "../../../components/buttons/CustomFilesUploadButton";
import { PROJECT_STATUS } from "../../../config/appConstants";

const TYPE_LIST = [
  { label: "Vehicle Systems", value: 400 },
  { label: "Vehicle Components", value: 300 },
  { label: "EE Components", value: 200 },
  { label: "Software Modules", value: 100 },
];


const DOC_TYPE_LIST = [
  { label: "Regulatory Templates", value: "REGULATORYTEMPLATE" },
  { label: "Attributes", value: "ATTRIBUTE" },
  { label: "Ticket Attachments", value: "TICKETATTACHMENT" },
  {label: "Certificate", value:"CERTIFICATE"}
];

const ENTITY_MAP = {
  1: "Software Modules",
  2: "EE Components",
  3: "Vehicle Components",
  6: "Vehicle Systems",
  SWM: "Software Modules",
  EE: "EE Components",
  VC: "Vehicle Components",
  VS: "Vehicle Systems",
};

const MIME_TYPE_MAP = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  gif: "image/gif",
  svg: "image/svg+xml",
  pdf: "application/pdf",
};

function getModuleTypeFromTicketDetails(ticketDetails) {
  if (!ticketDetails) return null;
  const {
    ticket_type,
    swm_details,
    ee_details,
    vehicle_component_details,
    vehicle_systems,
  } = ticketDetails;

  if (ticket_type && ENTITY_MAP[ticket_type]) return ENTITY_MAP[ticket_type];
  if (swm_details?.length) return "Software Modules";
  if (ee_details?.length) return "EE Components";
  if (vehicle_component_details?.length) return "Vehicle Components";
  if (vehicle_systems?.length) return "Vehicle Systems";
  return null;
}

function getTypeByValue(value, item) {
  const type = TYPE_LIST.find(
    (itemObj) => itemObj.value === value || itemObj.value === Number(value),
  );
  if (type) return type.label;
  if (ENTITY_MAP[value]) return ENTITY_MAP[value];

  const ticketModuleType = getModuleTypeFromTicketDetails(item?.ticket_details);
  if (ticketModuleType) return ticketModuleType;

  if (typeof value === "string" && value) {
    const cleanVal = value.toUpperCase().replaceAll(/[\s_-]+/g, "");
    if (
      !["REGULATORYTEMPLATE", "ATTRIBUTE", "TICKETATTACHMENT", "CERTIFICATE"].includes(
        cleanVal,
      )
    ) {
      return value;
    }
  }

  return "";
}

function getDocumentType(item) {
  if (!item) return "";

  const docTypeStr = (
    item?.document_type ||
    item?.doc_type ||
    item?.documentType ||
    item?.type_of_document ||
    ""
  )
    .toString()
    .toUpperCase()
    .replaceAll(/[\s_-]+/g, "");

  if (docTypeStr.includes("CERTIFICATE")) return "Certificate";
  if (docTypeStr.includes("ATTRIBUTE")) return "Attribute";
  if (docTypeStr.includes("TICKET") || docTypeStr.includes("ATTACHMENT")) return "Ticket Attachments";
  if (docTypeStr.includes("REGULATORY") || docTypeStr.includes("TEMPLATE")) return "Regulatory Template";

  if (item?.certificate_name || item?.is_certificate || item?.certificate_url) {
    return "Certificate";
  }
  if (
    item?.attribute_heading ||
    item?.attribute_value ||
    item?.attribute_sub_type ||
    item?.attribute_name ||
    item?.attribute_id
  ) {
    return "Attribute";
  }
  if (item?.attachment_name || item?.attachment_meta) {
    return "Ticket Attachments";
  }
  if (isRegulatoryTemplate(item)) {
    return "Regulatory Template";
  }

  return "Regulatory Template";
}

function getAttachmentType(item) {
  if (!item) return "";
  const docType = getDocumentType(item);
  if (docType !== "Ticket Attachments") {
    return "";
  }

  const rawType =
    item?.attachment_type ||
    item?.attachment_meta?.attachment_type ||
    item?.ticket_attachment_type ||
    "";

  if (!rawType) return "";

  const typeStr = String(rawType).trim();
  const upper = typeStr.toUpperCase();
  if (upper === "UPLOADED") return "Uploaded";
  if (upper === "SIMULATION") return "Simulation";
  if (upper === "ATTRIBUTE") return "Attribute";

  return typeStr;
}

function getCardName(item) {
  return (
    item?.name ||
    item?.attachment_name ||
    item?.attribute_heading ||
    item?.template_name ||
    item?.ticket_details?.description ||
    item?.ticket_details?.summary ||
    (item?.ticket_details?.lr_details?.[0]?.req_number
      ? `Req: ${item.ticket_details.lr_details[0].req_number}`
      : "") ||
    "Untitled Document"
  );
}

function isRegulatoryTemplate(data) {
  return Boolean(
    data?.ticket_details ||
      data?.document_type === "REGULATORYTEMPLATE" ||
      data?.template_tag ||
      Array.isArray(data?.fields),
  );
}

function getFileTarget(data) {
  return (
    data?.attachment_meta?.url ||
    data?.url ||
    data?.attachment_name ||
    data?.attribute_value ||
    data?.simulation_file ||
    data?.attachment_meta?.filename ||
    data?.file_name ||
    (typeof data?.name === "string" && data.name.includes(".") ? data.name : "") ||
    ""
  );
}

function resolveMimeType(fileTarget, data) {
  const ext = (fileTarget || "").split(".").pop().toLowerCase();
  const format = (
    data?.file_format ||
    data?.attribute_sub_type ||
    ""
  ).toLowerCase();
  return MIME_TYPE_MAP[ext] || MIME_TYPE_MAP[format] || "application/pdf";
}

function createFileBlob(resp, mimeType) {
  if (resp instanceof Blob) {
    if (resp.type) {
      return resp;
    }
    return new Blob([resp], { type: mimeType });
  }
  return new Blob([resp], { type: mimeType });
}

const formatDocumentListData = (data) => {
  if (Array.isArray(data)) {
    return data;
  }
  if (data && typeof data === "object") {
    return [
      ...(data.regulatory_template || []).map((el) => ({
        ...el,
        document_type: el.document_type || "REGULATORYTEMPLATE",
      })),
      ...(data.ticket_attachment || []).map((el) => ({
        ...el,
        document_type: el.document_type || "TICKETATTACHMENT",
      })),
      ...(data.attribute || []).map((el) => ({
        ...el,
        document_type: el.document_type || "ATTRIBUTE",
      })),
      ...(data.certificate || []).map((el) => ({
        ...el,
        document_type: el.document_type || "CERTIFICATE",
      })),
      ...(data.certificates || []).map((el) => ({
        ...el,
        document_type: el.document_type || "CERTIFICATE",
      })),
    ];
  }
  return [];
};

function DocumentLibraryIndex() {
  const dispatch = useDispatch();
  const selectedProjectDetails = useSelector(
    (state) => state.projectConfigReducer.selectedProjectDetails,
  );
  const selectedVariantDetails = useSelector(
    (state) => state.projectConfigReducer.selectedVariantDetails,
  );
  const [documentList, setDocumentList] = useState([]);
  const [singleFormDetails, setSingleFormDetails] = useState({});
  const [updateFormDetails] = useState({});
  const [updateForm, setUpdateForm] = useState(false);
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedSortObj, setSelectedSortObj] = useState({
    sortBy: "created_at",
    sortOrder: "DESC",
  });
  const [selectedSortObjBeforeClick, setSelectedSortObjBeforeClick] = useState({
    sortBy: "created_at",
    sortOrder: "DESC",
  });
  const [anchorFilter, setAnchorFilter] = useState(null);
  const [openFilter, setOpenFilter] = useState(false);
  const [openNewFormView, setOpenNewFormView] = useState(false);
  const [selectedTypeFilter, setSelectedTypeFilter] = useState([
    { label: "Vehicle Systems", value: 400 },
    { label: "Vehicle Components", value: 300 },
    { label: "EE Components", value: 200 },
    { label: "Software Modules", value: 100 },
  ]);
  const [selectedTypeFilterBeforeClick, setSelectedTypeFilterBeforeClick] =
    useState([
      { label: "Vehicle Systems", value: 400 },
      { label: "Vehicle Components", value: 300 },
      { label: "EE Components", value: 200 },
      { label: "Software Modules", value: 100 },
    ]);

  const [selectedDocTypeFilter, setSelectedDocTypeFilter] = useState(DOC_TYPE_LIST);
  const [selectedDocTypeFilterBeforeClick, setSelectedDocTypeFilterBeforeClick] =
    useState(DOC_TYPE_LIST);
  const [canProjectComplete, setCanProjectComplete] = useState(false);
  const [isCertificateUploaded, setIsCertificateUploaded] = useState(false);

  const isProjectCompleted =
    selectedProjectDetails?.status === PROJECT_STATUS?.COMPLETE ||
    Number(selectedProjectDetails?.status) === PROJECT_STATUS?.COMPLETE ||
    Number(selectedProjectDetails?.project_status) === PROJECT_STATUS?.COMPLETE ||
    selectedProjectDetails?.status === "COMPLETE" ||
    selectedProjectDetails?.project_status === "COMPLETE";

  const openRemoteFileInTab = async (fileTarget, dataId) => {
    const newTab = window.open("about:blank", "_blank");

    try {
      const resp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...testCasesActions.get_files,
            params: { filename: fileTarget },
            is_get_file: true,
          }),
        ),
      );

      if (!resp) {
        if (newTab) newTab.close();
        toast.error("Failed to load file");
        return;
      }

      const mimeType = resolveMimeType(fileTarget, dataId);
      const blob = createFileBlob(resp, mimeType);
      const fileUrl = URL.createObjectURL(blob);

      if (newTab) {
        newTab.location.href = fileUrl;
      } else {
        window.open(fileUrl, "_blank");
      }
    } catch (error) {
      console.error("Error opening file:", error);
      if (newTab) newTab.close();
      toast.error("Failed to open file");
    }
  };

  const handleOpen = async (dataId) => {
    if (isRegulatoryTemplate(dataId)) {
      setSingleFormDetails(dataId);
      if (dataId?.template_tag === "NEW") {
        setOpenNewFormView(true);
      } else {
        setOpen(true);
      }
      return;
    }

    const fileTarget = getFileTarget(dataId);
    if (!fileTarget) {
      setSingleFormDetails(dataId);
      setOpen(true);
      return;
    }

    const isDirectUrl = /^(https?:|blob:|data:)/.test(fileTarget);
    if (isDirectUrl) {
      window.open(fileTarget, "_blank");
      return;
    }

    await openRemoteFileInTab(fileTarget, dataId);
  };

  const handleFilterIconClick = (event) => {
    setAnchorFilter(event.currentTarget);
    setOpenFilter(true);
  };

  const handleFilterIconClose = () => {
    setAnchorFilter(null);
    setOpenFilter(false);
  };

  const handleApplyFilters = async (action) => {
    switch (action) {
      case "discard":
        setSelectedSortObjBeforeClick({
          sortBy: "created_at",
          sortOrder: "DESC",
        });
        setSelectedTypeFilterBeforeClick([
          { label: "Vehicle Systems", value: 400 },
          { label: "Vehicle Components", value: 300 },
          { label: "EE Components", value: 200 },
          { label: "Software Modules", value: 100 },
        ]);
        setSelectedDocTypeFilterBeforeClick(DOC_TYPE_LIST);
        break;

      case "cancel":
        handleFilterIconClose();
        break;

      case "apply":
        setSelectedSortObj(selectedSortObjBeforeClick);
        setSelectedTypeFilter(selectedTypeFilterBeforeClick);
        setSelectedDocTypeFilter(selectedDocTypeFilterBeforeClick);
        setPage(1);
        handleFilterIconClose();
        break;

      default:
        break;
    }
  };
  const handleClose = (index) => {
    if (index === 1) {
      setOpen(false);
    } else if (index === 2) {
      setUpdateForm(false);
    } else if (index === 3) {
      setOpenNewFormView(false);
    }
  };

  const handleCertificateUpload = async (files) => {
    const file = files?.[0];
    if (!file) return;

    try {
      const res = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...testCasesActions.upload_files,
            show_toast: true,
            file,
            is_file: true,
          }),
        ),
      );

      const returnedDocName = res?.file_name || res?.data?.file_name || res?.name;
      console.log("Uploaded document name:", returnedDocName, res);

      const projectId =
        selectedProjectDetails?.project_id ||
        selectedProjectDetails?.id ||
        "1147";

      if (returnedDocName) {
        const uploadResp = await Promise.resolve(
          dispatch(
            HandleApiActions({
              ...projectConfigAPI.upload_certificate,
              params: {
                project_id: String(projectId),
                certificate_name: returnedDocName,
              },
              show_toast: true,
            }),
          ),
        );
        if (!uploadResp?.error) {
          setIsCertificateUploaded(true);
          await fetchDocuments();
          checkCanComplete().catch(console.error);
        }
      }
    } catch (error) {
      console.error("Certificate upload error:", error);
      if (error?.response?.status === 413) {
        toast.error("Request Entity Too Large. File size exceeds the allowed limit.");
      } else {
        toast.error(error?.response?.data?.msg || error?.message || "Failed to upload certificate");
      }
    }
  };

  const checkCanComplete = useCallback(async () => {
    const projectId =
      selectedProjectDetails?.project_id || selectedProjectDetails?.id;
    if (!projectId) {
      setCanProjectComplete(false);
      setIsCertificateUploaded(false);
      return;
    }

    try {
      const resp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...projectConfigAPI.check_project_can_complete,
            params: { project_id: projectId },
            show_toast: false,
          }),
        ),
      );

      setCanProjectComplete(
        Boolean(
          resp?.data?.can_project_complete ?? resp?.data?.is_project_complete,
        ),
      );
      setIsCertificateUploaded(
        Boolean(resp?.data?.is_certificate_uploaded),
      );
    } catch (err) {
      console.error("checkProjectCanComplete error:", err);
      setCanProjectComplete(false);
      setIsCertificateUploaded(false);
    }
  }, [selectedProjectDetails, dispatch]);

  useEffect(() => {
    checkCanComplete().catch(console.error);
  }, [checkCanComplete]);

  const fetchDocuments = useCallback(async () => {
    const projectId =
      selectedProjectDetails?.project_id ||
      selectedProjectDetails?.id;
    const variantId =
      selectedVariantDetails?.variant_id ||
      selectedVariantDetails?.id;

    if (!projectId) return;

    try {
      const resp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...documentLibraryAction.get_df_reports,
            params: {
              project_id: Number.parseInt(projectId, 10),
              ...(Number.isInteger(Number.parseInt(variantId, 10)) && {
                variant_id: Number.parseInt(variantId, 10),
              }),
              is_ticket_required: true,
              page_number: page,
              page_size: rowsPerPage,
              [selectedSortObj.sortBy]: selectedSortObj.sortOrder,
              type: selectedTypeFilter.map((item) => item.value).join(","),
              document_type: selectedDocTypeFilter
                .map((item) => item.value)
                .join(","),
            },
            show_toast: false,
          }),
        ),
      );

      const list = formatDocumentListData(resp?.data);
      setDocumentList(list);
      setTotalCount(resp?.total_count || list.length);
    } catch (err) {
      console.error("fetchDocuments error:", err);
    }
  }, [
    dispatch,
    selectedProjectDetails,
    selectedVariantDetails,
    page,
    rowsPerPage,
    selectedSortObj,
    selectedTypeFilter,
    selectedDocTypeFilter,
  ]);

  useEffect(() => {
    dispatch(
      HandleApiActions({
        ...dynamicTemplateAction.get_d_template,
        params: {
          page_number: page,
          page_size: rowsPerPage,
        },
      }),
    );
  }, [open, updateForm, page, rowsPerPage, dispatch]);
  return (
    <Box sx={{ p: 1 }}>
      <Grid container>
        <Grid item sm={12} sx={{ p: 1 }}>
          <CommonBreadcrumbs
            items={[{ label: "Document Library" }, { label: "Report" }]}
          />
        </Grid>
        <Grid item sm={12}>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <CustomFilterV2
              anchorEl={anchorFilter}
              open={openFilter}
              widthPerColumn="250px"
              handleClick={handleFilterIconClick}
              handleClose={handleFilterIconClose}
              handleFilterButton={handleApplyFilters}
              showClearAll={true}
              handleClearAll={() => handleApplyFilters("discard")}
              sections={[
                {
                  heading: "Sorting",
                  content: (
                    <SortTemplate
                      defaultValue={selectedSortObjBeforeClick}
                      handleOnChange={(sortValue) => {
                        setSelectedSortObjBeforeClick(sortValue);
                      }}
                    />
                  ),
                },
                {
                  heading: "Filter by Type",
                  content: (
                    <FilterTemplate
                      data={[
                        {
                          label: "Software Module",
                          value: 100,
                        },
                        {
                          label: "EE Component",
                          value: 200,
                        },
                        {
                          label: "Vehicle Components",
                          value: 300,
                        },
                        {
                          label: "Vehicle Systems",
                          value: 400,
                        },
                      ]}
                      handleOnChange={(value) => {
                        setSelectedTypeFilterBeforeClick(value);
                      }}
                      defaultValue={selectedTypeFilterBeforeClick}
                    />
                  ),
                },
                {
                  heading: "Filter by Document Type",
                  content: (
                    <FilterTemplate
                      data={DOC_TYPE_LIST}
                      handleOnChange={(value) => {
                        setSelectedDocTypeFilterBeforeClick(value);
                      }}
                      defaultValue={selectedDocTypeFilterBeforeClick}
                    />
                  ),
                },
              ]}
            />
            <Box>
              <ProjectvariantSearchbar displayProjectAlone={true}/>
            </Box>

          </Box>
        </Grid>
        <Grid item sm={12} sx={{ display: "flex", justifyContent: "flex-end", gap:1 }}>
          <Box>
            <CustomFilesUploadButton 
              btnName={isCertificateUploaded ? "Update Certificate" : "Upload Certificate"}
              multiple={false}
              disabled={!canProjectComplete || isProjectCompleted}
              onFilesSelected={handleCertificateUpload}
            />
          </Box>
          <Box sx={{ width: "300px" }}>
            <CustomSearchbarWithApiV2
              apiAction={documentLibraryAction.get_df_reports}
              getDataOnSearch={(data, resp) => {
                const list = formatDocumentListData(data);
                setDocumentList(list);
                setTotalCount(resp?.total_count || list.length);
              }}
              placeholder={"Search Document Name"}
              searchKey={"name"}
              params={{
                project_id: Number.parseInt(selectedProjectDetails?.project_id, 10),
                ...(Number.isInteger(Number.parseInt(selectedVariantDetails?.variant_id, 10)) && {
                  variant_id: Number.parseInt(selectedVariantDetails?.variant_id, 10),
                }),
                is_ticket_required: true,
                page_number: page,
                page_size: rowsPerPage,
                [selectedSortObj.sortBy]: selectedSortObj.sortOrder,
                type: selectedTypeFilter.map((item) => item.value).join(","),
                document_type: selectedDocTypeFilter.map((item) => item.value).join(","),
              }}
            />
          </Box>
        </Grid>
        <Grid
          container
          spacing={4}
          sx={{
            p: 1,
            ml: 2,
            mr: 2,
            mt: 2,
            height: "475px",
            overflow: "auto",
          }}
        >
          {documentList.length > 0 ? (
            documentList.map((item, index) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={6}
                lg={3}
                key={
                  item?.id ||
                  item?.form_id ||
                  item?.report_id ||
                  item?.ticket_details?.intr_id ||
                  item?.intr_id ||
                  `${item?.document_type || "doc"}-${index}`
                }
              >
                <DocumentLibraryCard
                  name={getCardName(item)}
                  moduleType={getTypeByValue(
                    item?.type || item?.ticket_type || item?.entity_type,
                    item,
                  )}
                  documentType={getDocumentType(item)}
                  attachmentType={getAttachmentType(item)}
                  handleOpen={handleOpen}
                  data={item}
                />
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <Typography>No Data Found</Typography>
              </Box>
            </Grid>
          )}
        </Grid>
        <Grid item sm={12}>
          <CustomPaginationV2
            page={page}
            rowsPerPage={rowsPerPage}
            totalItems={totalCount}
            onPageChange={(newPage) => {
              setPage(newPage);
            }}
          />
        </Grid>
      </Grid>
      {open && (
        <ViewDynammicFormDetails
          data={singleFormDetails}
          open={open}
          handleClose={() => handleClose(1)}
        />
      )}
      {openNewFormView && (
        <PreviewModal
          isOpen={openNewFormView}
          onClose={() => handleClose(3)}
          pages={singleFormDetails?.fields || []}
          title={singleFormDetails?.name}
        />
      )}
      <UpdateDynamicForm
        data={updateFormDetails}
        open={updateForm}
        handleClose={() => handleClose(2)}
      />
    </Box>
  );
}

export default DocumentLibraryIndex;
